import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { AlertCircle, Loader2, UserPlus, Search, X, Trash2, Check, Users } from 'lucide-react';
import { toast } from 'sonner';
import {
  adminSearchUsers,
  adminGrantVideoAccess,
  adminListVideoGrants,
  adminRevokeGrant,
} from '@/lib/api';

const MAX_USERS = 20;
const MAX_DAYS = 365;

function defaultEndsAt(days = 30) {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() + days);
  // format for datetime-local input
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}T${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`;
}

function formatDateTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('es-ES', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch (_) {
    return iso;
  }
}

function isExpired(iso) {
  if (!iso) return false;
  return new Date(iso) <= new Date();
}

export function AdminVideoGrantsDialog({ open, onOpenChange, videoId, videoTitle }) {
  const [search, setSearch] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [selected, setSelected] = useState([]); // [{id, email, full_name}]
  const [endsAt, setEndsAt] = useState(defaultEndsAt(30));
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [lastResults, setLastResults] = useState(null); // {results: []}
  const [grants, setGrants] = useState([]);
  const [loadingGrants, setLoadingGrants] = useState(false);

  const canSearch = search.trim().length >= 3;
  const canSubmit = selected.length > 0 && endsAt && !submitting;

  const refreshGrants = useCallback(async () => {
    if (!videoId) return;
    setLoadingGrants(true);
    try {
      const data = await adminListVideoGrants(videoId);
      setGrants(data?.items || []);
    } catch (e) {
      toast.error(e?.message || 'No se pudo cargar el listado de concesiones.');
    } finally {
      setLoadingGrants(false);
    }
  }, [videoId]);

  useEffect(() => {
    if (open && videoId) {
      // reset form state when opening
      setSearch('');
      setSearchResults([]);
      setSelected([]);
      setReason('');
      setEndsAt(defaultEndsAt(30));
      setLastResults(null);
      refreshGrants();
    }
  }, [open, videoId, refreshGrants]);

  const handleSearch = async () => {
    if (!canSearch) return;
    setSearching(true);
    try {
      const data = await adminSearchUsers(search.trim(), 10);
      setSearchResults(data?.items || []);
    } catch (e) {
      toast.error(e?.message || 'Búsqueda fallida.');
    } finally {
      setSearching(false);
    }
  };

  const toggleSelected = (u) => {
    setSelected((prev) => {
      if (prev.find((x) => x.id === u.id)) {
        return prev.filter((x) => x.id !== u.id);
      }
      if (prev.length >= MAX_USERS) {
        toast.error(`Máximo ${MAX_USERS} usuarios por operación.`);
        return prev;
      }
      return [...prev, u];
    });
  };

  const removeSelected = (uid) => {
    setSelected((prev) => prev.filter((x) => x.id !== uid));
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    // Convert local datetime-local to UTC ISO
    let endsIso;
    try {
      endsIso = new Date(endsAt).toISOString();
    } catch (_) {
      toast.error('Fecha de caducidad no válida.');
      return;
    }
    setSubmitting(true);
    try {
      const data = await adminGrantVideoAccess(
        videoId,
        selected.map((u) => u.id),
        endsIso,
        reason.trim() || null,
      );
      setLastResults(data);
      const emailById = Object.fromEntries(selected.map((u) => [u.id, u.email]));
      const granted = (data?.results || []).filter((r) => r.status === 'granted').length;
      const already = (data?.results || []).filter((r) => r.status === 'already_granted').length;
      const errors = (data?.results || []).filter((r) => r.status === 'error').length;
      toast.success(`Procesado: ${granted} concedido(s), ${already} ya existente(s), ${errors} error(es).`);
      // attach emails to results for display
      data.results = (data?.results || []).map((r) => ({ ...r, email: emailById[r.user_id] || r.user_id }));
      setLastResults(data);
      setSelected([]);
      await refreshGrants();
    } catch (e) {
      toast.error(e?.message || 'No se pudo conceder el acceso.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRevoke = async (grantId) => {
    if (!window.confirm('¿Revocar esta concesión? El usuario perderá el acceso gratuito a este vídeo.')) return;
    try {
      await adminRevokeGrant(grantId);
      toast.success('Concesión revocada.');
      await refreshGrants();
    } catch (e) {
      toast.error(e?.message || 'No se pudo revocar.');
    }
  };

  // derived: max datetime-local
  const maxEndsAt = useMemo(() => defaultEndsAt(MAX_DAYS), []);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl bg-white max-h-[90vh] overflow-y-auto" data-testid="admin-video-grants-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5 text-[var(--ns-gold-700)]" />
            Accesos gratuitos — {videoTitle || 'vídeo'}
          </DialogTitle>
          <DialogDescription>
            Conceder visualización gratuita a usuarios concretos. No incluye descarga.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Search */}
          <div>
            <Label className="text-sm font-medium">Buscar usuarios (mínimo 3 caracteres)</Label>
            <div className="flex gap-2 mt-1">
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
                placeholder="Email o nombre"
                data-testid="grants-search-input"
              />
              <Button
                onClick={handleSearch}
                disabled={!canSearch || searching}
                variant="outline"
                data-testid="grants-search-btn"
              >
                {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                <span className="ml-1">Buscar</span>
              </Button>
            </div>
            {searchResults.length > 0 && (
              <div className="mt-2 border rounded-md divide-y bg-white max-h-48 overflow-y-auto" data-testid="grants-search-results">
                {searchResults.map((u) => {
                  const isSel = !!selected.find((x) => x.id === u.id);
                  return (
                    <div
                      key={u.id}
                      className="flex items-center justify-between p-2 hover:bg-[var(--ns-gold)]/10 cursor-pointer text-sm"
                      onClick={() => toggleSelected(u)}
                      data-testid={`grants-user-result-${u.id}`}
                    >
                      <div>
                        <div className="font-medium">{u.full_name || '(sin nombre)'}</div>
                        <div className="text-xs text-[var(--ns-text-muted)]">{u.email}</div>
                      </div>
                      {isSel ? (
                        <Badge className="bg-emerald-600 text-white">Añadido</Badge>
                      ) : (
                        <Button size="sm" variant="ghost">+ Añadir</Button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Selected list */}
          {selected.length > 0 && (
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium">Usuarios seleccionados ({selected.length}/{MAX_USERS})</Label>
              </div>
              <div className="flex flex-wrap gap-1.5 mt-2" data-testid="grants-selected-list">
                {selected.map((u) => (
                  <Badge
                    key={u.id}
                    variant="outline"
                    className="pr-1 bg-[var(--ns-gold)]/10 border-[var(--ns-gold)]/40"
                  >
                    {u.email}
                    <button
                      type="button"
                      onClick={() => removeSelected(u.id)}
                      className="ml-1 hover:text-red-600"
                      data-testid={`grants-remove-${u.id}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Expiration + reason */}
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <Label className="text-sm font-medium">Caduca el (máx. 365 días)</Label>
              <Input
                type="datetime-local"
                value={endsAt}
                max={maxEndsAt}
                onChange={(e) => setEndsAt(e.target.value)}
                data-testid="grants-ends-at"
              />
            </div>
            <div>
              <Label className="text-sm font-medium">Motivo (opcional)</Label>
              <Input
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="p.ej. regalo promocional"
                maxLength={500}
                data-testid="grants-reason"
              />
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cerrar</Button>
            <Button
              onClick={handleSubmit}
              disabled={!canSubmit}
              className="bg-gradient-to-r from-[var(--ns-gold)] to-[var(--ns-gold-600)] text-[#1c1612]"
              data-testid="grants-submit-btn"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Check className="h-4 w-4 mr-1" />}
              Conceder acceso
            </Button>
          </div>

          {/* Last operation results */}
          {lastResults && (
            <div className="border border-[var(--ns-gold)]/30 rounded-md p-3 bg-[var(--ns-gold)]/5">
              <div className="text-sm font-medium mb-2">Resultado de la operación</div>
              <ul className="space-y-1 text-sm" data-testid="grants-last-results">
                {(lastResults.results || []).map((r, i) => (
                  <li key={i} className="flex justify-between">
                    <span className="truncate mr-2">{r.email || r.user_id}</span>
                    {r.status === 'granted' ? (
                      <Badge className="bg-emerald-600 text-white">Concedido</Badge>
                    ) : r.status === 'already_granted' ? (
                      <Badge className="bg-amber-500 text-white">Ya existente</Badge>
                    ) : (
                      <Badge className="bg-red-600 text-white">{r.error_code || 'error'}</Badge>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <Separator />

          {/* Existing grants */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-semibold flex items-center gap-1.5">
                <Users className="h-4 w-4" /> Concesiones activas y pasadas ({grants.length})
              </h4>
              {loadingGrants && <Loader2 className="h-4 w-4 animate-spin" />}
            </div>
            {grants.length === 0 ? (
              <div className="text-sm text-[var(--ns-text-muted)] italic">
                Todavía no se ha concedido acceso gratuito a este vídeo.
              </div>
            ) : (
              <div className="border rounded-md divide-y max-h-64 overflow-y-auto" data-testid="grants-list">
                {grants.map((g) => {
                  const expired = isExpired(g.ends_at);
                  const revoked = g.status === 'revoked';
                  const active = !revoked && !expired && g.status === 'active';
                  return (
                    <div key={g.grant_id} className="flex items-center justify-between p-2 text-sm" data-testid={`grants-row-${g.grant_id}`}>
                      <div className="min-w-0 pr-2">
                        <div className="truncate font-medium">{g.user_email || g.user_id}</div>
                        <div className="text-xs text-[var(--ns-text-muted)]">
                          Caduca: {formatDateTime(g.ends_at)}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {revoked ? (
                          <Badge className="bg-slate-500 text-white">Revocada</Badge>
                        ) : expired ? (
                          <Badge className="bg-slate-400 text-white">Caducada</Badge>
                        ) : active ? (
                          <Badge className="bg-emerald-600 text-white">Activa</Badge>
                        ) : (
                          <Badge variant="outline">{g.status}</Badge>
                        )}
                        {active && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleRevoke(g.grant_id)}
                            className="h-7 px-2 text-red-600 hover:bg-red-50"
                            data-testid={`grants-revoke-${g.grant_id}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
